import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import DefaultAuth from "layouts/auth/Default";
import { acceptInvitation, getInvitation } from "services/authService";
import { ROLE_DISPLAY_NAMES } from "constants/roles";
import illustration from "assets/img/auth/auth.png";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const token = searchParams.get("token") || "";

  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const textColorBrand = useColorModeValue("brand.500", "white");

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        setErrors({ general: "Invitation link is missing a token." });
        setLoading(false);
        return;
      }

      const result = await getInvitation(token);
      if (result.success) {
        setInvitation(result.data);
      } else {
        setErrors({ general: result.error || "Invitation link is invalid or has expired." });
      }
      setLoading(false);
    };

    loadInvitation();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};

    if (!password) {
      nextErrors.password = "Password is required";
    } else if (password.length < 8) {
      nextErrors.password = "Password must be at least 8 characters";
    }

    if (!confirmPassword) {
      nextErrors.confirmPassword = "Confirm your password";
    } else if (password !== confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    setErrors({});

    const result = await acceptInvitation(token, password);
    if (result.success) {
      toast({
        title: "Password set",
        description: result.message || "You can now sign in.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      navigate("/auth/sign-in", { replace: true });
    } else {
      setErrors({ general: result.error || "Failed to set password." });
      toast({
        title: "Invitation failed",
        description: result.error || "Failed to set password.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

    setSubmitting(false);
  };

  const expiryText = invitation?.expires_at
    ? new Date(invitation.expires_at).toLocaleString()
    : "";

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w="100%"
        mx={{ base: "auto", lg: "0px" }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection="column">
        <Box me="auto">
          <Heading color={textColor} fontSize="36px" mb="10px">
            Set Password
          </Heading>
          <Text mb="28px" ms="4px" color={textColorSecondary} fontWeight="400" fontSize="md">
            Complete your AFRIQ NW account setup.
          </Text>
        </Box>

        {loading ? (
          <Flex align="center" gap="12px" color={textColorSecondary}>
            <Spinner size="sm" />
            <Text>Checking invitation...</Text>
          </Flex>
        ) : errors.general && !invitation ? (
          <Box w={{ base: "100%", md: "420px" }}>
            <Alert status="error" borderRadius="12px" mb="20px">
              <AlertIcon />
              <Text fontSize="sm">{errors.general}</Text>
            </Alert>
            <NavLink to="/auth/sign-in">
              <Text color={textColorBrand} fontSize="sm" fontWeight="600">
                Return to sign in
              </Text>
            </NavLink>
          </Box>
        ) : (
          <Box as="form" onSubmit={handleSubmit} w={{ base: "100%", md: "420px" }}>
            <Box mb="24px">
              <Text color={textColor} fontSize="md" fontWeight="700">
                {invitation.name}
              </Text>
              <Text color={textColorSecondary} fontSize="sm">
                {invitation.email}
              </Text>
              <Text color={textColorSecondary} fontSize="sm">
                {ROLE_DISPLAY_NAMES[invitation.role] || invitation.role}
                {invitation.center ? ` - ${invitation.center}` : ""}
              </Text>
              {expiryText && (
                <Text color={textColorSecondary} fontSize="xs" mt="6px">
                  Expires {expiryText}
                </Text>
              )}
            </Box>

            {errors.general && (
              <Alert status="error" borderRadius="12px" mb="20px">
                <AlertIcon />
                <Text fontSize="sm">{errors.general}</Text>
              </Alert>
            )}

            <FormControl isInvalid={!!errors.password} mb="24px">
              <FormLabel color={textColor} fontSize="sm" fontWeight="500">
                Password
              </FormLabel>
              <InputGroup>
                <Input
                  variant="auth"
                  fontSize="sm"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  size="lg"
                />
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: "pointer" }}
                    as={showPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={() => setShowPassword((value) => !value)}
                  />
                </InputRightElement>
              </InputGroup>
              {errors.password && <Text color="red.500" fontSize="xs" mt="6px">{errors.password}</Text>}
            </FormControl>

            <FormControl isInvalid={!!errors.confirmPassword} mb="24px">
              <FormLabel color={textColor} fontSize="sm" fontWeight="500">
                Confirm Password
              </FormLabel>
              <InputGroup>
                <Input
                  variant="auth"
                  fontSize="sm"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                  size="lg"
                />
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: "pointer" }}
                    as={showConfirmPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={() => setShowConfirmPassword((value) => !value)}
                  />
                </InputRightElement>
              </InputGroup>
              {errors.confirmPassword && <Text color="red.500" fontSize="xs" mt="6px">{errors.confirmPassword}</Text>}
            </FormControl>

            <Button
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              w="100%"
              h="50"
              mb="24px"
              type="submit"
              isLoading={submitting}
              loadingText="Setting password">
              Set Password
            </Button>

            <NavLink to="/auth/sign-in">
              <Text color={textColorBrand} fontSize="sm" fontWeight="600">
                Already set your password? Sign in
              </Text>
            </NavLink>
          </Box>
        )}
      </Flex>
    </DefaultAuth>
  );
}

export default AcceptInvite;
